import signup from '~/libs/legacy-redirects/signup.js'
import { useDealsStore } from '~/store/deals'
import { useRootStore } from '~/store/root'
import { useBorrowerStore } from '~/store/borrower'
import { useAffiliateCustomizationStore } from '~/store/affiliate-customization'
import env from '~/libs/env'

export default defineNuxtRouteMiddleware(async (to) => {
  const nuxtApp = useNuxtApp()
  const { $pinia, $lendioCookies, $applicationConstants } = nuxtApp
  const { experienceBaseRoute, nuxtBaseUrl } = $applicationConstants
  const piniaRootStore = useRootStore($pinia)
  const dealsStore = useDealsStore($pinia)
  const borrowerStore = useBorrowerStore($pinia)
  const affiliateCustomizationStore = useAffiliateCustomizationStore($pinia)
  const isEmbedded = piniaRootStore.isEmbedded;
  const isIL = affiliateCustomizationStore.isIntelligentLendingAffiliate;
  const req = import.meta.server
    ? nuxtApp.ssrContext.event.node.req
    : null
  const config = useRuntimeConfig()
  const useDocSuccessPage = config.public.useDocsOnlyFlow === 'true'
  const defaultAppRedirect = useDocSuccessPage ? '/documents' : '/resume-app'
  const appCompleteRedirect = getAppCompleteRedirect(isEmbedded, isIL)
  const rules = [
    {
      regExp: /.*\/overview.*/,
      redirect: '/portal'
    },
    {
      regExp: /.*\/continue.*/,
      redirect: '/portal'
    },
    {
      regExp: /.*\/no-cc-options.*/,
      redirect: experienceBaseRoute
    },
    {
      regExp: /.*\/app-complete-landing\/?$/,
      redirect: appCompleteRedirect,
    },
    ...signup,
  ]

  const rule = to.path ? rules.find(testRule.bind(null, to.path)) : null

  if (rule) {
    $lendioCookies.set('referral_url', req?.headers?.referer || null)

    const path = typeof rule.redirect === 'object'
      ? rule.redirect.path
      : rule.redirect
    const query = typeof rule.redirect === 'object'
      ? Object.assign({}, to.query, rule.redirect.query || {})
      : to.query

    const redirect = path +'?'+ Object.entries(query).map(([k, v]) => `${k}=${v}`).join(',')

    return nuxtApp.runWithContext(() => navigateTo(redirect, { redirectCode: 301, replace: true }))
  }

  const redirectMap = {
    '/basic-info': {
      path: experienceBaseRoute,
      options: { redirectCode: 301, replace: true },
    },
    '/business-credit-cards': {
      path: `${env('hpBaseUrl')}/business-loans/business-credit-card/`,
      options: { external: true, replace: true },
    },
    '/covid-relief/ppp-dashboard': {
      path: '/portal/options?currentApplication=\'PPP\'',
      options: { redirectCode: 301, replace: true },
    },
    '/covid-relief/thank-you': {
      path: '/portal/options?currentApplication=\'PPP\'',
      options: { redirectCode: 301, replace: true },
    },
    '/dashboard': {
      path: '/portal',
      options: { redirectCode: 301, replace: true },
    },
    '/update-info': {
      path: '/updated-info',
      options: { redirectCode: 301, replace: true },
    },
    '/portal/offers': {
      path: { path: '/portal/options', query: to.query, hash: to.hash },
      options: { redirectCode: 307, replace: true },
    },
    '/portal/loan-offers': {
      path: { path: '/portal/options', query: to.query, hash: to.hash },
      options: { redirectCode: 307, replace: true },
    },
    '/profile': {
      path: '/portal/settings',
      options: { redirectCode: 301, replace: true },
    },
    '/portal/profile': {
      path: '/portal/settings',
      options: { redirectCode: 301, replace: true },
    },
  };

  if (to.path in redirectMap) {
    const { path, options } = redirectMap[to.path]
    return nuxtApp.runWithContext(() => navigateTo(path, options))
  }

  if (/\/renewal(\/|$)/.exec(to.path) && piniaRootStore.authUser) {
    if (!dealsStore?.deals) {
      await dealsStore.getDealsWithOffers()
    }

    const hasFundedMPDeal = dealsStore?.deals?.length && dealsStore?.deals?.some(deal => /funded/i.exec(deal.stage))

    if (!hasFundedMPDeal || !borrowerStore?.borrower?.applicationComplete) {
      return nuxtApp.runWithContext(() => navigateTo(experienceBaseRoute))
    }
  }

  // MONITORING: Checking for routes that contain double baseUrls (/bp/bp). Should be removed after investigation.
  if (to.path.startsWith(`${nuxtBaseUrl}${nuxtBaseUrl}`)) {
    const newPath = parseInternalRedirect(to.path)
    log.warning('Detected double baseUrl in path', { originalPath: to.path, newPath })  
    return nuxtApp.runWithContext(() => navigateTo(newPath, { redirectCode: 301, replace: true }))
  }
})

function testRule(path, rule) {
  if (rule.hpRedirect && rule.regExp.test(path)) {
    return rule
  } else {
    return rule.regExp.test(path)
  }
}

function getAppCompleteRedirect(isEmbedded, isIntelligentLending) {
  if (isEmbedded) {
    return '/embedded/portal'
  }

  if (isIntelligentLending) {
    return '/intelligent-lending/portal'
  }

  return '/portal'
}
